// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
import { Box, Chip } from '@mui/material';
import type { ChipProps } from '@mui/material';
import { Edit, Eye, Clock, CheckCircle, AlertTriangle, X, FileText } from 'lucide-react';

/**
 * The invoice payment-status badge, extracted from InvoicesPage's
 * `getStatusBadge`. Decoupled from the invoice object: the caller passes the
 * already-derived `isQuote` / `overdue` flags so this stays a pure presentational
 * leaf. Lives under the financial-only-allowlisted `pages/invoices/` tree (it
 * renders no money — just the status chip).
 */

// Inline @keyframes for the OVERDUE badge's pulse (was Tailwind animate-pulse).
const pulse = {
  animation: 'invpulse 2s cubic-bezier(0.4,0,0.6,1) infinite',
  '@keyframes invpulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.5 } },
} as const;

// Maps payment status → MUI Chip color + icon (was per-status Tailwind classes).
const statusConfig: Record<string, { color: ChipProps['color']; icon: typeof Edit }> = {
  draft: { color: 'default', icon: Edit },
  sent: { color: 'primary', icon: Eye },
  viewed: { color: 'secondary', icon: Eye },
  partial_payment: { color: 'warning', icon: Clock },
  paid: { color: 'success', icon: CheckCircle },
  unpaid: { color: 'error', icon: Clock },
  overdue: { color: 'error', icon: AlertTriangle },
  cancelled: { color: 'default', icon: X },
};

export function InvoiceStatusBadge({
  status,
  isQuote = false,
  overdue = false,
}: {
  status: string;
  isQuote?: boolean;
  overdue?: boolean;
}) {
  // Quotes show a single "Quote" badge instead of a payment status.
  if (isQuote) {
    return <Chip size="small" color="primary" icon={<FileText size={12} />} label="Quote" />;
  }

  const config = statusConfig[status] || statusConfig.unpaid;
  const Icon = config!.icon;

  // An overdue unpaid invoice shows both an "unpaid" and a pulsing "OVERDUE" badge.
  if (overdue && status === 'unpaid') {
    return (
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        <Chip size="small" color="error" icon={<Clock size={12} />} label="unpaid" />
        <Chip size="small" color="error" icon={<AlertTriangle size={12} />} label="OVERDUE" sx={{ fontWeight: 600, ...pulse }} />
      </Box>
    );
  }

  return (
    <Chip size="small" color={config!.color ?? 'default'} icon={<Icon size={12} />} label={status.replace('_', ' ')} />
  );
}
